<template>
  <div>
  <v-data-table
    dense
    :headers="headers"
    :items="internalValue"
    :items-per-page="-1"
    hide-default-footer
    class="elevation-1 CreditDetails"
  >
    <template v-slot:top>
      <div class="d-flex justify-end">
        <v-btn
          class="mx-2" color="primary"
          fab icon x-small
          @click="addOne"
          @keydown.enter.stop.prevent="addOne"
        >
          <v-icon>
            {{icons.mdiPlus}}
          </v-icon>
        </v-btn>
        <FortePayment
          v-if="!isPayrix &&
           renderComponent && forteConfig.isConfigured"
          @forteResponse="forteResponse"
        />
      </div>
    </template>

    <template v-slot:[`item.cardType`]="{ item }">
      <v-select
        dense
        class="credit-card-type"
        v-model="item.cardType"
        :items="cardTypes"
        :rules="cardTypeValidation"
        @change="val => somethingChanged()"
        item-text="description"
        item-value="name"
      ></v-select>
    </template>

    <template v-slot:[`item.amount`]="{ item, index }">
      <v-text-field
        dense
        v-model="item.amount"
        :rules="amountValidation"
        single-line
        class="credit-amount"
        @keydown="shortcutHandler($event, index)"
        @keydown.enter="somethingChanged"
        @blur="somethingChanged();sumAmount()"
      ></v-text-field>
    </template>

    <template v-slot:[`item.comment`]="{ item }">
      <v-text-field
        dense
        v-model="item.comment"
        single-line
        class="eft-comment"
        @keydown.enter="somethingChanged"
        @blur="somethingChanged()"
      ></v-text-field>
    </template>

    <template v-slot:[`item.actions`]="{ item, index }">
      <v-btn v-if="isPayrix"
        class="input-text-size" dense
        color="primary" @click="handlePayrix(index)">
        Proceed to pay
      </v-btn>
      <v-icon
        medium class="mr-2"
        tabindex="-1"
        @click="removeOne(item)"
      >
        {{icons.mdiDelete}}
      </v-icon>
    </template>

    <template v-slot:[`item.cleared`]="{ item }">
      <v-checkbox
        small class="mr-2 od-credit-iscleared-checkbox"
        v-model="item.isCleared"
        @change="somethingChanged"
      ></v-checkbox>
    </template>

    <template slot="body.append">
      <tr class="blue--text">
          <td colspan="2" id="total-credit-amount">
            Total : ${{ amountFormat(amountRemaining) }}
          </td>
      </tr>
    </template>

  </v-data-table>
  <PayrixPaymentDialog
  v-if="isDialogOpen"
  :fieldNameLists="['Deposit Amount','Convenience fee (credit)','Convenience fee (debit)',]"
  />
</div>
</template>

<script>
import {
  mdiPlus,
  mdiDelete,
} from '@mdi/js';

import { mapActions, mapMutations, mapState } from 'vuex';

import {
  isTabOrPlus,
  amountToNumber,
  amountFormat,
} from '../../util/shared/vue-global';

import FortePayment from '../common/FortePayment.vue';
import PayrixPaymentDialog from '../common/PayrixPaymentDialog.vue';

const defaultItem = {
  cardType: undefined,
  amount: 0,
};

export default {
  name: 'CreditDetails',
  components: {
    FortePayment,
    PayrixPaymentDialog,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    componentKey: Number,
    depositAmount: [Number, String],
    depositData: Object, // parent DepositDetails internalValue
  },
  async created() {
    await this.loadConfig({ requiredScope: 'odconfig' });
    await this.loadConfig({ requiredScope: 'paymentConfig' });
  },
  data: () => ({
    icons: {
      mdiPlus,
      mdiDelete,
    },
    totalAmount: 0,
    convenienceFee: 0,
    headers: [
      { text: 'Credit Card Type', value: 'cardType' },
      { text: 'Amount', value: 'amount' },
      { text: 'Comment', value: 'comment', sortable: false },
      { text: 'Actions', value: 'actions', sortable: false },
      { text: 'Cleared', value: 'cleared', sortable: false },
    ],
    renderComponent: true,
  }),
  computed: {
    ...mapState({
      cardTypes: (state) => state.enums.ODCardItemTypeEnum,
      odSettingItem: (state) => state.SystemConfig.odSettingItem,
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem || {},
      paymentSettingItem: (state) => state.SystemConfig.paymentSettingItem || {},
      payrixSuccessFlag: (state) => state.common.payrixPayment.paymentSuccessFlag,
      isDialogOpen: (state) => state.common.payrixPayment.isDialogOpen,
    }),
    internalValue: {
      get() {
        const newVal = JSON.parse(JSON.stringify(this.value));
        const updatedValue = newVal.map(this.loadInitialValues);
        return updatedValue;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    forteConfig() {
      return this.$store.state.ForteService.forteConfig;
    },
    cardTypeValidation() {
      return [
        (value) => !!value || 'Required',
      ];
    },
    amountValidation() {
      return [
        (value) => !!value || 'Required',
        (value) => value !== 0 || 'Should be non-zero',
      ];
    },
    amountRemaining() {
      let totalRemainingAmount = amountToNumber(this.depositAmount);
      for (let i = 0; i < (this.internalValue || []).length; i += 1) {
        totalRemainingAmount -= amountToNumber(this.internalValue[i].amount);
      }
      return totalRemainingAmount;
    },
    isPayrix() {
      const settings = this.odSettingItem;
      const isPayrixGateway = settings.selectedGateway === 'payrix';
      const hasConfig = !!settings.payrixMerchentId && !!settings.payrixApiKey;
      const result = isPayrixGateway && hasConfig;
      return result;
    },
  },
  watch: {
    componentKey() {
      this.forceRerender();
    },
    payrixSuccessFlag: {
      immediate: false,
      handler(val) {
        if (val) {
          this.$emit('payrixSuccess');
        }
      },
    },
  },
  methods: {
    ...mapActions('SystemConfig', ['loadConfig']),
    ...mapActions('common/payrixPayment', [
      'setPayrixPaymentData',
      'setPayrixPaymentType',
      'setPaymentContext',
      'setOdDepositData',
      'toggleDialog',
    ]),
    ...mapMutations('base/crud', ['setIsVisible']),
    amountToNumber,
    amountFormat,
    loadInitialValues(item) {
      return {
        ...item,
        amount: amountFormat(amountToNumber(item.amount)),
      };
    },
    shortcutHandler(e, index) {
      if (isTabOrPlus(e)) {
        if ((index + 1) === (this.internalValue || []).length) {
          this.sumAmount();
          this.somethingChanged();
          if (amountToNumber(this.depositAmount) > this.sumAmount()) {
            this.addOne();
          }
        }
      }
    },
    addOne() {
      this.$emit('input', [
        ...this.internalValue,
        {
          ...defaultItem,
        },
      ]);
      return true;
    },
    removeOne(item) {
      // const indexOfRemove = this.internalValue.indexOf(item);
      this.$emit('input', [
        ...this.internalValue.filter((candidate) => item !== candidate),
      ]);
    },
    somethingChanged() {
      this.$emit('input', this.internalValue);
    },
    forteResponse(response) {
      this.$emit('forteResponse', response); // this is a trace number that gets emitted from CreditDetails to DepositDetails after payments is made
    },
    forceRerender() {
      // Remove forte-component from the DOM
      this.renderComponent = false;

      this.$nextTick(() => {
        // Add the forte-component back in
        this.renderComponent = true;
      });
    },
    sumAmount() {
      let totalSum = 0.00;
      for (let i = 0; i < this.internalValue.length; i += 1) {
        totalSum += amountToNumber(this.internalValue[i].amount);
      }
      return totalSum;
    },
    async handlePayrix(index) {
      const amt = amountToNumber(this.internalValue[index].amount);
      if (amt && amt > 0) {
        const creditFeePercentage = amountToNumber(this.paymentSettingItem.payrixCreditPercentage);
        const debitFeePercentage = amountToNumber(this.paymentSettingItem.payrixDebitPercentage);
        const debitFeeFlat = amountToNumber(this.paymentSettingItem.payrixDebitFeeFlat);
        const debitAmtLimit = amountToNumber(this.paymentSettingItem.payrixDebitAmtLimit);
        const convenienceFeeCredit = amountToNumber((amt * creditFeePercentage) / 100);
        const totalPlusCreditFee = amountToNumber(amt + convenienceFeeCredit);
        const eCheckFee = amountToNumber(this.paymentSettingItem.payrixEcheckFee);
        const totalPlusEcheckFee = amountToNumber(amt + eCheckFee);

        let convenienceFeeDebit = 0;

        if (debitAmtLimit > 0 && amt < debitAmtLimit) {
          convenienceFeeDebit = debitFeeFlat;
        } else {
          convenienceFeeDebit = amountToNumber((amt * debitFeePercentage) / 100);
        }
        const totalPlusDebitFee = amountToNumber(amt + convenienceFeeDebit);
        const payrixPaymentData = {
          totalAmount: amt,
          convenienceFeeCredit,
          totalPlusCreditFee,
          convenienceFeeDebit,
          totalPlusDebitFee,
          creditFeePercentage,
          debitFeePercentage,
          debitFeeFlat,
          eCheckFee,
          totalPlusEcheckFee,
        };
        await this.setPaymentContext('OD');
        await this.setOdDepositData(this.depositData);
        await this.setPayrixPaymentType('CREDIT');
        await this.setPayrixPaymentData(payrixPaymentData);
        await this.toggleDialog(true);
      } else {
        alert('Please enter a valid amount');
      }
    },
  },
};
</script>

<style lang="sass">
  .credit-amount input
    text-align: right
</style>
